import React, { useState, useEffect, useRef } from 'react';
import { apiUrl } from '../../utils/apiBase';
import { Plus, Edit, Trash2, Eye, Send, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { UserSession } from '../../types';
import { useLoading } from '../../contexts/LoadingContext';
import Toggle from '../common/Toggle';

interface EmailTemplate {
  id: number;
  nome: string;
  assunto: string;
  conteudo: string;
  destinatarios: string[];
  cc: string[];
  bcc: string[];
  campos_utilizados: string[];
  ativo: boolean;
  created_at: string;
  updated_at: string;
  // Persistência das seleções no editor
  fluxoId?: string | null;
  etapaId?: string | null;
}

interface Fluxo {
  id: string;
  nome: string;
  descricao: string;
}

interface Etapa {
  id: string;
  nome: string;
  posicao: number;
  responsavel?: string;
}

interface Campo {
  nome: string;
  tipo: string;
  descricao: string;
  obrigatorio?: boolean;
  opcoes?: string[];
  formato?: string;
}

interface EmailsProps {
  session: UserSession | null;
}

const Emails: React.FC<EmailsProps> = ({ session }) => {
  const { showLoading, hideLoading, loading } = useLoading();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [fluxos, setFluxos] = useState<Fluxo[]>([]);
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [campos, setCampos] = useState<Campo[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  // Seletores hierárquicos
  const [selectedFluxo, setSelectedFluxo] = useState<string | null>(null);
  const [selectedEtapa, setSelectedEtapa] = useState<string | null>(null);
  const initializingFromTemplate = useRef(false);

  // Toggle helpers (ativar/desativar)
  const toggleTemplateAtivo = async (id: number, novoAtivo: boolean) => {
    try {
  const response = await fetch(apiUrl(`/templates-email/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({
            email: session?.usuario?.email,
            role: session?.usuario?.role,
            empresaId: session?.empresa?.id || session?.usuario?.empresaId
          })
        },
        body: JSON.stringify({ ativo: novoAtivo })
      });
      const data = await response.json();
      if (response.ok && data?.success) {
        setTemplates(prev => prev.map(t => (String(t.id) === String(id) ? { ...t, ativo: novoAtivo } : t)));
      } else {
        console.warn('Falha ao alternar template', id, data);
      }
    } catch (e) {
      console.error('Erro ao alternar template', e);
    }
  };

  const toggleAllTemplates = async (ativar: boolean) => {
    try {
      // Atualiza visualmente primeiro para resposta ágil
      setTemplates(prev => prev.map(t => ({ ...t, ativo: ativar })));
      // Persistir em paralelo (um PUT por template)
      await Promise.all(
        templates.map(t =>
          fetch(apiUrl(`/templates-email/${t.id}`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Data': JSON.stringify({
                email: session?.usuario?.email,
                role: session?.usuario?.role,
                empresaId: session?.empresa?.id || session?.usuario?.empresaId
              })
            },
            body: JSON.stringify({ ativo: ativar })
          }).catch(() => null)
        )
      );
    } catch (e) {
      console.error('Erro ao alternar todos os templates', e);
    }
  };

  // Referências para os campos de texto onde inserir as variáveis
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const contentTextareaRef = useRef<HTMLTextAreaElement>(null);
  // Formulário para criar/editar template
  const [formData, setFormData] = useState({
    nome: '',
    assunto: '',
    conteudo: '',
    destinatarios: [''],
    cc: [''],
    bcc: [''],
    ativo: true,
    fluxoId: null as string | null,
    etapaId: null as string | null
  });

  // Preview
  const [previewData, setPreviewData] = useState({
    assunto: '',
    conteudo: '',
    html: ''
  });
  useEffect(() => {
    carregarTemplates();
    if (session) {
      carregarFluxos();
    }
  }, [session]);


  // Utilitário: normaliza resposta de /api/flows (pode vir como array simples ou {success,data})
  const parseFlowsResponse = (data: any) => {
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.flows && Array.isArray(data.flows)) return data.flows;
    return [] as any[];
  };

  const carregarFluxos = async () => {
    try {
      if (!session) {
        console.log('Nenhuma sessão ativa, não carregando fluxos');
        return;
      }

      // Usar o endpoint correto de flows do MongoDB
      const empresaId = session.empresa?.id;
      const url = empresaId 
        ? apiUrl(`/flows?empresaId=${empresaId}`)
        : apiUrl('/flows');
      
      console.log('Carregando fluxos para templates de email:', { 
        usuario: session.usuario.nome, 
        empresaId,
        url 
      });
      
  const response = await fetch(url);
  const data = await response.json();
      
  // Aceitar tanto array puro quanto objeto com {success,data}
  let flows = parseFlowsResponse(data);
  if (flows && Array.isArray(flows)) {
        console.log('Fluxos recebidos do MongoDB:', flows.length);
        
        // Filtrar fluxos baseado nas permissões do usuário
        const usuarioLogado = session.usuario.nome;
        const emailUsuario = session.usuario.email;
        
        // Para Beatriz, mostrar apenas fluxos André e Beatriz 
        // Para outros usuários, aplicar filtros adequados
        const fluxosFiltrados = flows.filter((flow: any) => {
          const nomeFluxo = flow.name || flow.nome || '';
          
          // Log para debug
          console.log(`Verificando fluxo: ${nomeFluxo} para usuário: ${usuarioLogado}`);
          
          // Regra específica para Beatriz - mostrar apenas André e Beatriz
          if (usuarioLogado === 'BEATRIZ SILVEIRA' || emailUsuario === 'marketing@refricril.com.br') {
            const isAllowed = nomeFluxo.toLowerCase().includes('andré') || 
                             nomeFluxo.toLowerCase().includes('andre') ||
                             nomeFluxo.toLowerCase().includes('beatriz');
            console.log(`  Beatriz - Fluxo ${nomeFluxo} permitido: ${isAllowed}`);
            return isAllowed;
          }
          
          // Para outros usuários, mostrar todos os fluxos da empresa por enquanto
          // TODO: Implementar lógica de permissões mais específica
          return true;
        });
        
        console.log(`Fluxos filtrados para ${usuarioLogado}:`, fluxosFiltrados.length);
        fluxosFiltrados.forEach((flow: any) => {
          console.log(`  - ${flow.name || flow.nome}`);
        });
        
        setFluxos(fluxosFiltrados.map((f: any) => ({
          id: f.id || f._id, // servidor já define 'id'; manter fallback
          nome: f.name || f.nome,
          descricao: f.descricao || f.description || ''
        })));
      } else {
        console.error('Erro ao carregar fluxos: resposta vazia ou inválida');
      }
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    }
  };

  const carregarEtapas = async (fluxoId: string) => {
    try {
      // Buscar o fluxo específico do MongoDB (mesmo endpoint, parse genérico)
      const empresaId = session?.empresa?.id;
      const url = empresaId 
        ? apiUrl(`/flows?empresaId=${empresaId}`)
        : apiUrl('/flows');
      
      const response = await fetch(url);
      const data = await response.json();
      const flows = parseFlowsResponse(data);
      
      if (Array.isArray(flows)) {
        const flow = flows.find((f: any) => String(f.id || f._id) === String(fluxoId));
        if (flow) {
          const etapas = flow.etapas || flow.stages || [];
          
          const etapasFormatadas = etapas.map((etapa: any, index: number) => ({
            id: etapa.id || etapa.nome || etapa.name || `etapa-${index}`, // Usar nome como fallback se não tiver ID
            nome: etapa.nome || etapa.name || `Etapa ${index + 1}`,
            posicao: index,
            responsavel: etapa.responsavel
          }));
          
          setEtapas(etapasFormatadas);
        } else {
          setEtapas([]);
        }
      } else {
        setEtapas([]);
      }
    } catch (error) {
      console.error('Erro ao carregar etapas:', error);
      setEtapas([]);
    }
  };

  const carregarCampos = async (fluxoId: string, etapaId: string) => {
    try {
      // Buscar o fluxo específico do MongoDB (mesmo endpoint, parse genérico)
      const empresaId = session?.empresa?.id;
      const url = empresaId 
        ? apiUrl(`/flows?empresaId=${empresaId}`)
        : apiUrl('/flows');
      
      const response = await fetch(url);
      const data = await response.json();
      const flows = parseFlowsResponse(data);
      
      if (Array.isArray(flows)) {
        const flow = flows.find((f: any) => String(f.id || f._id) === String(fluxoId));
        if (flow) {
          const etapas = flow.etapas || flow.stages || [];
          
          const etapa = etapas.find((e: any, index: number) => {
            const idEtapa = e.id || e.nome || e.name || `etapa-${index}`;
            return String(idEtapa) === String(etapaId);
          });
          
          if (etapa) {
            const campos = etapa.campos || [];
            
            const camposFormatados = campos.map((campo: any) => ({
              nome: campo.nome || campo.name,
              tipo: campo.tipo || campo.type,
              descricao: campo.descricao || campo.description || campo.nome || campo.name,
              obrigatorio: campo.obrigatorio || campo.required,
              opcoes: campo.opcoes || campo.options || []
            }));
            
            setCampos(camposFormatados);
            return;
          }
        }
      }
      
      // Se não encontrou campos, limpar
      setCampos([]);
    } catch (error) {
      console.error('Erro ao carregar campos:', error);
      setCampos([]);
    }
  };

  const salvarTemplate = async () => {
    showLoading('configuracoes', 'Salvando template', 'Processando dados do template...');
    try {
      const url = editingTemplate 
        ? apiUrl(`/templates-email/${editingTemplate.id}`)
        : apiUrl('/templates-email');
      
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({
            email: session?.usuario?.email,
            role: session?.usuario?.role,
            empresaId: session?.empresa?.id || session?.usuario?.empresaId
          })
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      if (data.success) {
        await carregarTemplates();
        fecharModal();
      } else {
        alert('Erro ao salvar template: ' + data.error);
      }
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      alert('Erro ao salvar template');
    } finally {
      hideLoading();
    }
  };

  const deletarTemplate = async (id: number) => {
    if (!confirm('Tem certeza que deseja deletar este template?')) return;
    
    try {
  const response = await fetch(apiUrl(`/templates-email/${id}`), {
        method: 'DELETE',
        headers: {
          'X-User-Data': JSON.stringify({
            email: session?.usuario?.email,
            role: session?.usuario?.role,
            empresaId: session?.empresa?.id || session?.usuario?.empresaId
          })
        }
      });
      
      const data = await response.json();
      if (data.success) {
        await carregarTemplates();
      } else {
        alert('Erro ao deletar template: ' + data.error);
      }
    } catch (error) {
      console.error('Erro ao deletar template:', error);
      alert('Erro ao deletar template');
    }
  };
  const gerarPreview = async () => {
    try {
  const response = await fetch(apiUrl('/preview-template'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({
            email: session?.usuario?.email,
            role: session?.usuario?.role,
            empresaId: session?.empresa?.id || session?.usuario?.empresaId
          })
        },
        body: JSON.stringify({
          template_content: formData.conteudo,
          template_subject: formData.assunto,
          field_values: {} // Preview sem valores de teste
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setPreviewData(data.preview);
        setPreviewMode(true);
      }
    } catch (error) {
      console.error('Erro ao gerar preview:', error);
    }
  };

  const enviarTeste = async (templateId: number) => {
    try {
  const response = await fetch(apiUrl('/enviar-email-template'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Data': JSON.stringify({
            email: session?.usuario?.email,
            role: session?.usuario?.role,
            empresaId: session?.empresa?.id || session?.usuario?.empresaId
          })
        },
        body: JSON.stringify({
          template_id: templateId,
          field_values: {} // Envio sem valores de teste
        })
      });
      
      const data = await response.json();
      if (data.success) {
        alert('E-mail de teste enviado com sucesso!');
      } else {
        alert('Erro ao enviar teste: ' + data.error);
      }
    } catch (error) {
      console.error('Erro ao enviar teste:', error);
      alert('Erro ao enviar teste');
    }
  };

  const testarEmail = async () => {
    console.log('🧪 Iniciando teste de email SMTP...');
    console.log('👤 Session atual:', session);
    console.log('🔐 Role do usuário:', session?.usuario?.role);
    
    try {
      const url = apiUrl('/teste-email');
      console.log('📍 URL da requisição:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('📥 Status da resposta:', response.status);
      console.log('📋 Headers da resposta:', response.headers);
      
      const result = await response.json();
      
      console.log('📧 Resposta do teste de email:', result);
      
      if (response.ok && result.success) {
        console.log('✅ Teste de email bem-sucedido:', result.message);
        alert(`✅ ${result.message}\n\nDestinatário: ${result.recipient}\nServidor: ${result.config_used?.server}:${result.config_used?.port}`);
      } else {
        console.error('❌ Erro no teste de email:', result);
        const errorDetails = `
❌ Erro no teste de email:
${result.error}

Tipo: ${result.error_type || 'N/A'}
Servidor: ${result.config?.server || 'N/A'}:${result.config?.port || 'N/A'}
Timeout: ${result.config?.timeout || 'N/A'}s

Verifique os logs do servidor para mais detalhes.`;
        
        alert(errorDetails);
      }
    } catch (error) {
      console.error('💥 Erro ao executar teste de email:', error);
      alert(`💥 Erro ao executar teste de email:\n${error}`);
    }
  };

  const abrirModal = (template?: EmailTemplate) => {
    if (template) {
      initializingFromTemplate.current = true;
      setEditingTemplate(template);
      setFormData({
        nome: template.nome,
        assunto: template.assunto,
        conteudo: template.conteudo,
        destinatarios: template.destinatarios,
        cc: template.cc,
        bcc: template.bcc,
        ativo: template.ativo,
        fluxoId: template.fluxoId ? String(template.fluxoId) : null,
        etapaId: template.etapaId ? String(template.etapaId) : null
      });
      setSelectedFluxo(template.fluxoId ? String(template.fluxoId) : null);
      setSelectedEtapa(template.etapaId ? String(template.etapaId) : null);
    } else {
      setEditingTemplate(null);
      setFormData({
        nome: '',
        assunto: '',
        conteudo: '',
        destinatarios: [''],
        cc: [''],
        bcc: [''],
        ativo: true,
        fluxoId: null,
        etapaId: null
      });
      setSelectedFluxo(null);
      setSelectedEtapa(null);
    }
    setShowModal(true);
    setPreviewMode(false);
    setCampos([]);
    setEtapas([]);
  };

  const fecharModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setPreviewMode(false);
    setSelectedFluxo(null);
    setSelectedEtapa(null);
    setCampos([]);
    setEtapas([]);
  };

  const adicionarCampo = (tipo: 'destinatarios' | 'cc' | 'bcc') => {
    setFormData(prev => ({
      ...prev,
      [tipo]: [...prev[tipo], '']
    }));
  };

  const removerCampo = (index: number, tipo: 'destinatarios' | 'cc' | 'bcc') => {
    setFormData(prev => ({
      ...prev,
      [tipo]: prev[tipo].filter((_, i) => i !== index)
    }));
  };

  const atualizarCampo = (index: number, valor: string, tipo: 'destinatarios' | 'cc' | 'bcc') => {
    setFormData(prev => ({
      ...prev,
      [tipo]: prev[tipo].map((item, i) => i === index ? valor : item)
    }));
  };

  // Função para inserir variável no assunto ou conteúdo
  const inserirVariavel = (nomeCampo: string, destino: 'assunto' | 'conteudo') => {
    const variavel = `{{${nomeCampo}}}`;
    
    if (destino === 'assunto') {
      setFormData(prev => ({
        ...prev,
        assunto: prev.assunto + variavel
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        conteudo: prev.conteudo + variavel
      }));
    }
  };

  // Handlers para drag and drop
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, nomeCampo: string) => {
    e.dataTransfer.setData('text/plain', nomeCampo);
  };

  const handleDragOver = (e: React.DragEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLInputElement | HTMLTextAreaElement>, destino: 'assunto' | 'conteudo') => {
    e.preventDefault();
    const nomeCampo = e.dataTransfer.getData('text/plain');
    inserirVariavel(nomeCampo, destino);
  };

  useEffect(() => {
    if (selectedFluxo) {
      carregarEtapas(selectedFluxo);
      if (!initializingFromTemplate.current) {
        setSelectedEtapa(null);
        setCampos([]);
      }
    }
  }, [selectedFluxo]);

  useEffect(() => {
    if (selectedFluxo && selectedEtapa) {
      carregarCampos(selectedFluxo, selectedEtapa);
    }
  }, [selectedEtapa, selectedFluxo]);

  // Quando etapas carregarem na inicialização a partir do template, liberar flag
  useEffect(() => {
    if (initializingFromTemplate.current) {
      initializingFromTemplate.current = false;
    }
  }, [etapas]);

  // Sincronizar seleções com formData para persistir
  useEffect(() => {
    setFormData(prev => ({ ...prev, fluxoId: selectedFluxo }));
  }, [selectedFluxo]);
  useEffect(() => {
    setFormData(prev => ({ ...prev, etapaId: selectedEtapa }));
  }, [selectedEtapa]);

  const carregarTemplates = async () => {
    try {
      const empresaId = session?.empresa?.id || session?.usuario?.empresaId;
      const params = new URLSearchParams({ reload: '1' });
      if (empresaId) params.set('empresaId', empresaId);
      const response = await fetch(apiUrl(`/templates-email?${params.toString()}`));
      const data = await response.json();
      if (data.success) {
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  // Verificar se usuário é administrador
  const isAdmin = ['administrator', 'admin'].includes(session?.usuario?.role as any);

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-16">
          <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acesso Restrito</h2>
          <p className="text-gray-500">
            Apenas administradores podem gerenciar templates de e-mail.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </div>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Templates de E-mail</h1>
                <p className="text-xs text-gray-500">Configure templates e automações de e-mail</p>
              </div>
            </div>
            {(['administrator','admin'].includes(session?.usuario?.role as any)) && (
              <div className="flex items-center gap-3">
                <button
                  onClick={testarEmail}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <Edit className="h-4 w-4" />
                  Teste SMTP
                </button>
                <Toggle
                  checked={templates.some(t => t.ativo)}
                  onChange={() => toggleAllTemplates(!templates.some(t => t.ativo))}
                  size="md"
                  label={templates.some(t => t.ativo) ? 'Pausar todos' : 'Ativar todos'}
                />
                <button
                  onClick={() => abrirModal()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
                >
                  <Plus className="h-4 w-4" />
                  Novo Template
                </button>
              </div>
            )}
          </div>

          {/* Lista de Templates */}
          {templates.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <div className="w-16 h-16 mx-auto bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Nenhum template configurado</h3>
              <p className="text-sm text-gray-500">
                Comece criando seu primeiro template de e-mail
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {templates.map((template) => (
                <div key={template.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-200 hover:shadow-sm transition-all">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 text-sm">{template.nome}</h3>
                          <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium ${
                            template.ativo 
                              ? 'bg-green-50 text-green-700 border border-green-200' 
                              : 'bg-gray-50 text-gray-500 border border-gray-200'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${template.ativo ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                            {template.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        <p className="text-gray-600 text-xs mb-2">{template.assunto}</p>
                        <p className="text-[11px] text-gray-400 flex items-center gap-1">
                          <span>👥</span>
                          Destinatários: {template.destinatarios.join(', ')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      {(['administrator','admin'].includes(session?.usuario?.role as any)) && (
                        <>
                          <button
                            onClick={() => enviarTeste(template.id)}
                            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Enviar Teste"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => abrirModal(template)}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deletarTemplate(template.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Deletar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {(['administrator','admin'].includes(session?.usuario?.role as any)) && (
                    <div className="pt-3 mt-3 border-t border-gray-100">
                      <Toggle
                        checked={template.ativo}
                        onChange={(v) => toggleTemplateAtivo(template.id, v)}
                        label={template.ativo ? 'Pausar' : 'Ativar'}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Modal de Criação/Edição */}
          {showModal && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-xl border border-gray-200">
                <div className="p-5 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-bold text-gray-900">
                      {editingTemplate ? 'Editar Template' : 'Novo Template'}
                    </h2>
                    <div className="flex gap-2">
                      <button
                        onClick={gerarPreview}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        Preview
                      </button>
                      <button
                        onClick={fecharModal}
                        className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
          
          {/* Modal body continues below in existing code */}

                <div className="p-5">
                  {!previewMode ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Formulário */}
                      <div className="lg:col-span-2 space-y-5">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome do Template</label>
                          <input
                            type="text"
                            value={formData.nome}
                            onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                            className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
                            placeholder="Ex: Boas-vindas, Notificação de Status"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Assunto</label>
                          <input
                            ref={subjectInputRef}
                            type="text"
                            value={formData.assunto}
                            onChange={(e) => setFormData(prev => ({ ...prev, assunto: e.target.value }))}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, 'assunto')}
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white/80 backdrop-blur-sm"
                            placeholder="Use {{campo}} para inserir variáveis ou arraste campos da lateral"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Conteúdo HTML</label>
                          <textarea
                            ref={contentTextareaRef}
                            value={formData.conteudo}
                            onChange={(e) => setFormData(prev => ({ ...prev, conteudo: e.target.value }))}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, 'conteudo')}
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white/80 backdrop-blur-sm h-48 resize-none"
                            placeholder="Use {{campo}} para inserir variáveis dos fluxos ou arraste campos da lateral"
                          />
                        </div>

                        {/* Destinatários */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Destinatários</label>
                          {formData.destinatarios.map((dest, index) => (
                            <div key={index} className="flex gap-2 mb-3">
                              <input
                                type="email"
                                value={dest}
                                onChange={(e) => atualizarCampo(index, e.target.value, 'destinatarios')}
                                className="flex-1 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white/80 backdrop-blur-sm"
                                placeholder="email@exemplo.com ou {{email_cliente}}"
                              />
                              <button
                                onClick={() => removerCampo(index, 'destinatarios')}
                                className="text-red-500 hover:text-red-700 px-3 py-1 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => adicionarCampo('destinatarios')}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium py-2 px-4 rounded-xl hover:bg-blue-50 transition-colors"
                          >
                            + Adicionar Destinatário
                          </button>
                        </div>

                        {/* Botões */}
                        <div className="flex gap-3 pt-6 border-t border-gray-200">
                          <button
                            onClick={salvarTemplate}
                            disabled={loading.isLoading}
                            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 transition-all transform hover:scale-[1.02] font-medium"
                          >
                            {loading.isLoading ? 'Salvando...' : 'Salvar Template'}
                          </button>
                          <button
                            onClick={fecharModal}
                            className="border border-gray-200 px-8 py-3 rounded-xl hover:bg-gray-50 transition-colors font-medium text-gray-700"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>

                      {/* Campos Disponíveis - Estrutura Hierárquica */}
                      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                        <h3 className="font-semibold text-gray-900 mb-4 text-lg">Campos Disponíveis dos Fluxos</h3>
                        
                        {/* Seletor de Fluxo */}
                        <div className="mb-4">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">1. Selecione o Fluxo</label>
                          <select
                            value={selectedFluxo || ''}
                            onChange={(e) => setSelectedFluxo(e.target.value || null)}
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white/80 backdrop-blur-sm"
                          >
                            <option value="">Escolha um fluxo...</option>
                            {fluxos.map(fluxo => (
                              <option key={fluxo.id} value={fluxo.id}>
                                {fluxo.nome}
                              </option>
                            ))}
                          </select>
                        </div>

                    {/* Seletor de Etapa */}
                    {selectedFluxo && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">2. Selecione a Etapa</label>
                        <select
                          value={selectedEtapa || ''}
                          onChange={(e) => setSelectedEtapa(e.target.value || null)}
                          className="w-full border rounded px-3 py-2"
                        >
                          <option value="">Escolha uma etapa...</option>
                          {etapas.map(etapa => (
                            <option key={etapa.id} value={etapa.id}>
                              {etapa.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Lista de Campos */}
                    {selectedEtapa && campos.length > 0 && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">3. Campos da Etapa</label>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {campos.map(campo => (
                            <div
                              key={campo.nome}
                              draggable
                              onDragStart={(e) => handleDragStart(e, campo.nome)}
                              className="p-3 border rounded cursor-move hover:bg-gray-50 border-dashed border-gray-300"
                              onClick={() => inserirVariavel(campo.nome, 'conteudo')}
                            >
                              <div className="flex items-start gap-2">
                                <GripVertical size={14} className="text-gray-400 mt-1 flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{campo.descricao || campo.nome}</div>                                  <div className="text-xs text-gray-500">
                                    Variável: {`{{${campo.nome}}}`} • Tipo: {campo.tipo}
                                  </div>
                                  {campo.obrigatorio && (
                                    <div className="text-xs text-blue-600">Obrigatório</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-3 p-3 bg-blue-50 rounded text-sm">
                          <p className="font-medium mb-1">💡 Como usar:</p>
                          <p>• <strong>Clique</strong> no campo para inserir no conteúdo</p>
                          <p>• <strong>Arraste</strong> o campo para o assunto ou conteúdo</p>
                        </div>
                      </div>
                    )}

                    {/* Estado vazio */}
                    {!selectedFluxo && (
                      <div className="p-4 text-center text-gray-500">
                        <ChevronDown size={32} className="mx-auto mb-2 text-gray-300" />
                        <p>Selecione um fluxo para ver os campos disponíveis</p>
                      </div>
                    )}

                    {selectedFluxo && !selectedEtapa && (
                      <div className="p-4 text-center text-gray-500">
                        <ChevronRight size={32} className="mx-auto mb-2 text-gray-300" />
                        <p>Selecione uma etapa para ver os campos</p>
                      </div>
                    )}

                    {selectedFluxo && selectedEtapa && campos.length === 0 && (
                      <div className="p-4 text-center text-gray-500">
                        <p>Esta etapa não possui campos configurados</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Preview */
                <div>
                  <h3 className="font-medium mb-4">Preview do E-mail</h3>
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div className="mb-4">
                      <strong>Assunto:</strong> {previewData.assunto}
                    </div>
                    <div className="border bg-white p-4 rounded">
                      <div dangerouslySetInnerHTML={{ __html: previewData.html }} />
                    </div>
                  </div>
                  <button
                    onClick={() => setPreviewMode(false)}
                    className="mt-4 border px-4 py-2 rounded hover:bg-gray-50"
                  >
                    Voltar à Edição
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
  </div>
  );
};

export default Emails;
